import { useCallback, useEffect, useRef, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { AdminPagination } from "../AdminPagination";
import { formatShare, sortVersionsDesc } from "../clientVersionDisplay";

type Surface = "widget" | "mobile";

type VersionRow = {
  version: string;
  users: number;
  percentage: number;
  last_seen_at: string | null;
  outdated: boolean;
};

type Summary = {
  latest: Record<Surface, string | null>;
  surfaces: Record<Surface, VersionRow[]>;
};

type UserRow = {
  user_id: string;
  email: string;
  widget_version: string | null;
  widget_last_seen_at: string | null;
  mobile_version: string | null;
  mobile_last_seen_at: string | null;
};

type HistoryRow = {
  version: string;
  platform: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
};

const SURFACE_LABEL: Record<Surface, string> = {
  widget: "Widget (desktop)",
  mobile: "Mobiele app",
};

const LIMIT = 50;

export function VersionsSection() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<{ surface: Surface; version: string } | null>(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<Surface, HistoryRow[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Volgordebewaking: elke `load()`/`toggle()`-aanroep claimt een oplopend
  // sequencenummer. Als er een tragere, oudere aanvraag terugkomt nadat een
  // nieuwere al is gestart, wijkt haar sequencenummer af van het huidige en
  // wordt haar resultaat genegeerd — dat voorkomt dat een stale response een
  // nieuwer resultaat overschrijft (bijv. bij snel typen in het zoekveld, of
  // snel na elkaar twee gebruikersrijen uitklappen).
  const loadSeq = useRef(0);
  const historySeq = useRef(0);

  const load = useCallback(async () => {
    const seq = (loadSeq.current += 1);
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (filter) {
        params.set("surface", filter.surface);
        params.set("version", filter.version);
      }
      if (query.trim()) params.set("q", query.trim());
      const [sum, page] = await Promise.all([
        authFetchJson<Summary>("/api/admin/client-versions/summary"),
        authFetchJson<{ items: UserRow[]; total: number }>(
          `/api/admin/client-versions/users?${params.toString()}`,
        ),
      ]);
      if (loadSeq.current !== seq) return;
      setSummary(sum);
      setUsers(page.items ?? []);
      setTotal(page.total ?? 0);
    } catch (err) {
      if (loadSeq.current !== seq) return;
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      if (loadSeq.current === seq) setLoading(false);
    }
  }, [offset, filter, query]);

  useEffect(() => { void load(); }, [load]);

  const toggle = useCallback(async (userId: string) => {
    if (expanded === userId) {
      historySeq.current += 1;
      setExpanded(null);
      setHistory(null);
      return;
    }
    const seq = (historySeq.current += 1);
    setExpanded(userId);
    setHistory(null);
    try {
      const res = await authFetchJson<Record<Surface, HistoryRow[]>>(
        `/api/admin/client-versions/users/${encodeURIComponent(userId)}`,
      );
      if (historySeq.current !== seq) return;
      setHistory(res);
    } catch (err) {
      if (historySeq.current !== seq) return;
      setError(err instanceof Error ? err.message : "Historie laden mislukt.");
    }
  }, [expanded]);

  return (
    <section className="space-y-4">
      <p className="cs-caption">Versies</p>
      <p className="text-sm text-[var(--cs-ink-muted)]">
        Versies die de widget en de mobiele app melden. Per gebruiker en per app
        blijven de tien laatste versiewissels bewaard; wie meerdere computers
        gebruikt ziet daar meerdere versies naast elkaar. Apps melden zich
        hooguit één keer per uur, en oudere versies van vóór deze functie melden
        zich helemaal niet.
      </p>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading && !summary ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : null}

      {summary ? (
        <div className="grid gap-4 md:grid-cols-2">
          {(["widget", "mobile"] as Surface[]).map((surface) => {
            const rows = sortVersionsDesc(summary.surfaces[surface] ?? []);
            const latest = summary.latest[surface];
            return (
              <div
                key={surface}
                className="rounded-xl border border-[rgba(10,52,96,0.10)] bg-white p-4"
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold text-[var(--cs-navy)]">
                    {SURFACE_LABEL[surface]}
                  </h3>
                  {latest ? (
                    <span className="text-xs text-[var(--cs-ink-muted)]">
                      nieuwste release: v{latest}
                    </span>
                  ) : null}
                </div>
                {rows.length === 0 ? (
                  <p className="pt-3 text-sm text-[var(--cs-ink-muted)]">
                    Nog geen meldingen.
                  </p>
                ) : (
                  <ul className="space-y-2 pt-3">
                    {rows.map((row) => (
                      <li key={row.version}>
                        <button
                          type="button"
                          onClick={() => {
                            setOffset(0);
                            setFilter(
                              filter?.surface === surface && filter.version === row.version
                                ? null
                                : { surface, version: row.version },
                            );
                          }}
                          className="w-full text-left"
                        >
                          <div className="flex items-baseline justify-between text-sm">
                            <span
                              className={
                                row.outdated
                                  ? "font-medium text-amber-700"
                                  : "font-medium text-[var(--cs-navy)]"
                              }
                            >
                              v{row.version}
                              {row.outdated ? " · loopt achter" : ""}
                            </span>
                            <span className="tabular-nums text-xs text-[var(--cs-ink-muted)]">
                              {formatShare(row.users, row.percentage)}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-[rgba(10,52,96,0.08)]">
                            <div
                              className={
                                row.outdated
                                  ? "h-1.5 rounded-full bg-amber-500"
                                  : "h-1.5 rounded-full bg-[var(--cs-navy)]"
                              }
                              style={{ width: `${Math.max(row.percentage, 2)}%` }}
                            />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(event) => { setOffset(0); setQuery(event.target.value); }}
          placeholder="Zoek op e-mailadres"
          className="rounded-lg border border-[rgba(10,52,96,0.15)] px-3 py-1.5 text-sm"
        />
        {filter ? (
          <button
            type="button"
            onClick={() => { setOffset(0); setFilter(null); }}
            className="rounded-lg border border-[rgba(10,52,96,0.15)] px-3 py-1.5 text-xs"
          >
            Filter: {SURFACE_LABEL[filter.surface]} v{filter.version} — wissen
          </button>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-xl border border-[rgba(10,52,96,0.10)] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgba(10,52,96,0.08)] text-left text-xs uppercase tracking-wide text-[var(--cs-ink-muted)]">
              <th className="px-4 py-3">Gebruiker</th>
              <th className="px-4 py-3">Widget</th>
              <th className="px-4 py-3">Mobiel</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-[var(--cs-ink-muted)]" colSpan={4}>
                  Geen meldingen gevonden.
                </td>
              </tr>
            ) : (
              users.map((row) => (
                <tr key={row.user_id} className="border-b border-[rgba(10,52,96,0.06)] align-top">
                  <td className="px-4 py-3">{row.email || row.user_id}</td>
                  <td className="px-4 py-3">
                    {row.widget_version ? (
                      <>
                        v{row.widget_version}
                        <div className="text-xs text-[var(--cs-ink-muted)]">
                          {formatDateTime(row.widget_last_seen_at)}
                        </div>
                      </>
                    ) : (
                      <span className="text-[var(--cs-ink-muted)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.mobile_version ? (
                      <>
                        v{row.mobile_version}
                        <div className="text-xs text-[var(--cs-ink-muted)]">
                          {formatDateTime(row.mobile_last_seen_at)}
                        </div>
                      </>
                    ) : (
                      <span className="text-[var(--cs-ink-muted)]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void toggle(row.user_id)}
                      className="text-xs font-medium text-[var(--cs-navy)] underline"
                    >
                      {expanded === row.user_id ? "Verberg historie" : "Historie"}
                    </button>
                    {expanded === row.user_id ? (
                      <div className="mt-2 text-left">
                        {history === null ? (
                          <p className="text-xs text-[var(--cs-ink-muted)]">Laden…</p>
                        ) : (
                          (["widget", "mobile"] as Surface[]).map((surface) => (
                            <div key={surface} className="pb-2">
                              <p className="text-xs font-semibold text-[var(--cs-navy)]">
                                {SURFACE_LABEL[surface]}
                              </p>
                              {(history[surface] ?? []).length === 0 ? (
                                <p className="text-xs text-[var(--cs-ink-muted)]">Geen meldingen.</p>
                              ) : (
                                <ul className="text-xs text-[var(--cs-ink-muted)]">
                                  {(history[surface] ?? []).map((item) => (
                                    <li key={`${surface}-${item.version}-${item.first_seen_at}`}>
                                      v{item.version}
                                      {item.platform ? ` (${item.platform})` : ""} ·{" "}
                                      {formatDateTime(item.first_seen_at)} –{" "}
                                      {formatDateTime(item.last_seen_at)}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <AdminPagination
          offset={offset}
          limit={LIMIT}
          total={total}
          onPrev={() => setOffset(Math.max(0, offset - LIMIT))}
          onNext={() => setOffset(offset + LIMIT)}
        />
      </div>
    </section>
  );
}
